import { useCallback } from 'react'
import { assignWorkItem } from '../application/assign-work-item'
import { completeWorkItem } from '../application/complete-work-item'
import { cancelWorkItem } from '../application/cancel-work-item'
import { reopenWorkItem } from '../application/reopen-work-item'
import { acceptHandoff } from '../application/accept-handoff'
import { rejectHandoff } from '../application/reject-handoff'
import { archiveWorkItem } from '../application/archive-work-item'
import { addComment } from '../application/add-comment'
import { editComment } from '../application/edit-comment'
import { setSpawnLightweight } from '../application/set-spawn-lightweight'
import { concludeSpawn } from '../application/conclude-spawn'
import { setWorkState } from '../application/set-work-state'
import { resolveItemPhaseTeamId } from '../domain/authorization'
import type { Phase, WorkItem, WorkItemWorkState } from '../domain/workflow'
import { workItems$ } from '../store'
import { workItems as workItemsPort, notify } from '../wiring'
import { getWorkItemActor } from './use-work-item-actor'

const replaceInStore = (updated: WorkItem | null) => {
	if (updated) {
		workItems$.set(workItems$.get().map(w => (w.id === updated.id ? updated : w)))
	}
	return updated
}

// phases is optional: when omitted, phaseTeamId resolves to null and canActor
// falls back to permissive for system/unauthenticated actors (dev/seed flows).
const useWorkItemLifecycle = (phases?: Phase[]) => {
	const teamId = useCallback(
		(item: WorkItem) => (phases ? resolveItemPhaseTeamId(phases, item) : null),
		[phases],
	)

	const assign = useCallback(
		(item: WorkItem, member: { id: string; name: string }) =>
			assignWorkItem(workItemsPort, item, getWorkItemActor(), member, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const complete = useCallback(
		(item: WorkItem) =>
			completeWorkItem(workItemsPort, item, getWorkItemActor(), notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const cancel = useCallback(
		(item: WorkItem, reason: string) =>
			cancelWorkItem(workItemsPort, item, getWorkItemActor(), reason, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const reopen = useCallback(
		(item: WorkItem) =>
			reopenWorkItem(workItemsPort, item, getWorkItemActor(), notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const accept = useCallback(
		(item: WorkItem) =>
			acceptHandoff(workItemsPort, item, getWorkItemActor(), notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const reject = useCallback(
		(item: WorkItem, reason: string) =>
			rejectHandoff(workItemsPort, item, getWorkItemActor(), reason, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const archive = useCallback(
		(item: WorkItem, force: boolean) =>
			archiveWorkItem(workItemsPort, item, getWorkItemActor(), force, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const comment = useCallback(
		(item: WorkItem, body: string) =>
			addComment(workItemsPort, item, getWorkItemActor(), body, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const updateComment = useCallback(
		(item: WorkItem, commentId: string, body: string) =>
			editComment(workItemsPort, item, getWorkItemActor(), commentId, body, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const setLightweight = useCallback(
		(item: WorkItem, lightweight: boolean) =>
			setSpawnLightweight(workItemsPort, item, getWorkItemActor(), lightweight, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const conclude = useCallback(
		(item: WorkItem, status: 'completed' | 'cancelled') =>
			concludeSpawn(workItemsPort, item, getWorkItemActor(), status, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	const changeWorkState = useCallback(
		(item: WorkItem, workState: WorkItemWorkState) =>
			setWorkState(workItemsPort, item, getWorkItemActor(), workState, notify, teamId(item)).then(replaceInStore),
		[teamId],
	)

	return {
		assign,
		complete,
		cancel,
		reopen,
		accept,
		reject,
		archive,
		comment,
		updateComment,
		setLightweight,
		conclude,
		changeWorkState,
	}
}

export { useWorkItemLifecycle }
